import type { ColumnDef } from '@tanstack/react-table';
import { SETTINGS_PERMISSIONS } from '@/constants/permissions';
import { ActiveStatusBadge } from '@/components/common/ActiveStatusBadge';
import {
  createActionsColumn,
  createSerialColumn,
  WrappingCellText,
} from '@/components/common/DataTable';
import { EmptyValue } from '@/components/common/EmptyValue';
import { HighlightText } from '@/components/common/HighlightText';
import { RowEditDeleteButtons } from '@/components/common/RowActions';
import { CODE_BADGE_CLASS } from '@/styles/codeBadge';
import { formatDisplayDate } from '@/utils/date';
import type { FeedbackType } from '../types/feedbackType.types';

interface FeedbackTypeColumnActions {
  onEdit: (feedbackType: FeedbackType) => void;
  onDelete: (feedbackType: FeedbackType) => void;
}

export function getFeedbackTypeColumns(
  searchText?: string,
  actions?: FeedbackTypeColumnActions,
): ColumnDef<FeedbackType, unknown>[] {
  return [
    createSerialColumn<FeedbackType>(),
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => (
        <WrappingCellText className="font-medium">
          <HighlightText text={row.original.name} highlightedText={searchText} />
        </WrappingCellText>
      ),
    },
    {
      accessorKey: 'code',
      header: 'Code',
      cell: ({ row }) => (
        <WrappingCellText className={CODE_BADGE_CLASS}>
          <HighlightText text={row.original.code} highlightedText={searchText} />
        </WrappingCellText>
      ),
    },
    {
      accessorKey: 'description',
      header: 'Description',
      cell: ({ row }) => {
        const description = row.original.description;
        if (!description) return <EmptyValue />;
        return <WrappingCellText width="wide">{description}</WrappingCellText>;
      },
    },
    {
      accessorKey: 'isActive',
      header: 'Status',
      cell: ({ row }) => <ActiveStatusBadge isActive={row.original.isActive} />,
    },
    {
      accessorKey: 'createdAt',
      header: 'Created At',
      cell: ({ row }) => formatDisplayDate(row.original.createdAt),
    },
    ...(actions
      ? [
          createActionsColumn<FeedbackType>((feedbackType) => (
            <RowEditDeleteButtons
              item={feedbackType}
              itemName={feedbackType.name}
              onEdit={actions.onEdit}
              onDelete={actions.onDelete}
              editRequires={SETTINGS_PERMISSIONS.FEEDBACK_TYPES_EDIT}
              deleteRequires={SETTINGS_PERMISSIONS.FEEDBACK_TYPES_DELETE}
            />
          )),
        ]
      : []),
  ];
}
